import { type UseCartMutations } from '@hungryroot/cart';
import { Footer } from '@hungryroot/rdp';
import { type PairingV1 as Pairing } from '@hungryroot/rest';
import {
  type PairingWithIngredientDetails,
  type UseEditRecipeResult,
} from '../../../../hooks/useEditRecipe/useEditRecipe';
import { getDisplayPoints } from '../../../edit-recipe/PairingEdit/PairingEdit';
import { type RDPModalAnalytics } from '../hooks/useRDPModalAnalytics';
import { type RDPModalCart } from '../hooks/useRDPModalCart';
import { type RDPModalModals } from '../hooks/useRDPModalModals';

interface RDPModalFooterProps {
  orderId: number;
  currentPairing: PairingWithIngredientDetails;
  editRecipeQuery: UseEditRecipeResult;
  cartMutations: UseCartMutations | null;
  isAccountInActive: boolean;
  areAllIngredientsInStock: boolean;
  count: number;
  showServingSizeWarningModal: boolean;
  initialPairing: Pairing;
  onDismiss: () => void;
  analytics: RDPModalAnalytics;
  cart: RDPModalCart;
  modals: RDPModalModals;
  index: number | null;
}

export const RDPModalFooter = ({
  orderId,
  currentPairing,
  editRecipeQuery,
  cartMutations,
  isAccountInActive,
  areAllIngredientsInStock,
  count,
  showServingSizeWarningModal,
  initialPairing,
  onDismiss,
  analytics,
  cart,
  modals,
  index,
}: RDPModalFooterProps) => {
  const { id, name } = currentPairing;

  const handleIncrementClick = () => {
    analytics.trackAddToCart(id, index);
    analytics.trackInteraction({
      orderId,
      id,
      name,
      interactionType: 'add_to_cart',
    });

    if (isAccountInActive) {
      showServingSizeWarningModal
        ? modals.setIsServingSizeWarningModalOpen(true)
        : modals.setIsUpdateCartModalOpen(true);
      cart.updateCartCallback.current = cart.onAddClick;
    } else {
      cart.onAddClick(cartMutations);
    }
  };

  const handleAddClick = () => {
    analytics.trackAddToCart(id, index);
    analytics.trackInteraction({
      orderId,
      id,
      name,
      interactionType: 'add_to_cart',
    });

    if (isAccountInActive) {
      showServingSizeWarningModal
        ? modals.setIsServingSizeWarningModalOpen(true)
        : modals.setIsUpdateCartModalOpen(true);
      cart.updateCartCallback.current = (cartMutations) => {
        cart.onAddClick(cartMutations);
        onDismiss();
      };
    } else {
      cart.onAddClick(cartMutations);
      onDismiss();
    }
  };

  const handleRemoveClick = () => {
    analytics.trackRemoveFromCart(id, index);
    analytics.trackInteraction({
      orderId,
      id,
      name,
      interactionType: 'remove_from_cart',
    });

    if (isAccountInActive) {
      showServingSizeWarningModal
        ? modals.setIsServingSizeWarningModalOpen(true)
        : modals.setIsUpdateCartModalOpen(true);
      cart.updateCartCallback.current = cart.onRemoveClick;
    } else {
      cart.onRemoveClick(cartMutations);
    }
  };

  const handleSubtractClick = () => {
    analytics.trackRemoveFromCart(id, index);
    analytics.trackInteraction({
      orderId,
      id,
      name,
      interactionType: 'remove_from_cart',
    });

    if (isAccountInActive) {
      showServingSizeWarningModal
        ? modals.setIsServingSizeWarningModalOpen(true)
        : modals.setIsUpdateCartModalOpen(true);
      cart.updateCartCallback.current = cart.onSubtractClick;
    } else {
      cart.onSubtractClick(cartMutations);
    }
  };

  if (!orderId) return null;

  return (
    <Footer
      outOfStock={
        currentPairing.is_merchandized === false ||
        areAllIngredientsInStock === false
      }
      isLoading={cartMutations?.isMutating}
      onIncrementClick={handleIncrementClick}
      onAddClick={handleAddClick}
      onRemoveClick={handleRemoveClick}
      onSubtractClick={handleSubtractClick}
      creditsForItem={Number(
        getDisplayPoints({
          bundle: undefined,
          pairing: currentPairing,
          modifications: editRecipeQuery.mutations,
          initialPairing,
        })
      )}
      numInCart={count}
    />
  );
};
